import { doc, getDoc } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import ExecutionState from '@src/models/ExecutionState';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import safeExecute from '@src/util/safeExecute';

class ExecutionStateRepository {
  constructor() {
    this.cancelExecutionOp = API_OPERATIONS.CANCEL_EXECUTION;
    this.reportStallOp = API_OPERATIONS.REPORT_EXECUTION_STALL;
    this.endStallOp = API_OPERATIONS.END_EXECUTION_STALL;
    this.supervisorCloseOp = API_OPERATIONS.SUPERVISOR_CLOSE_EXECUTION;
  }

  /**
   * Get the path to the state subcollection
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {string} Path to state subcollection
   */
  getStatePath(organizationId, divisionId, executionId) {
    return `${FSCollections.ORGANIZATIONS}/${organizationId}/${FSCollections.DIVISIONS}/${divisionId}/${FSCollections.STATE}/${executionId}`;
  }

  /**
   * Get execution state from Firestore
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<ExecutionState|null>} ExecutionState instance or null if not found
   */
  async get(organizationId, divisionId, executionId) {
    return safeExecute(async () => {
      if (!organizationId || !divisionId || !executionId) {
        return null;
      }

      const stateDocRef = doc(
        db,
        this.getStatePath(organizationId, divisionId, executionId)
      );
      const stateDoc = await getDoc(stateDocRef);
      if (!stateDoc.exists()) {
        return null;
      }

      return ExecutionState.fromFirestore(
        stateDoc.data(),
        stateDocRef.id,
        organizationId,
        divisionId,
        executionId,
      );
    });
  }

  /**
   * Map Cloud Function error codes to user-friendly messages
   * @param {string} errorCode - Firebase error code
   * @returns {string} User-friendly error message
   */
  getErrorMessage(errorCode) {
    const errorMessages = {
      unauthenticated: 'User not authenticated',
      'invalid-argument': 'Missing or invalid parameters',
      'not-found': 'Execution not found',
      'failed-precondition': 'Invalid state transition',
      internal: 'Internal server error',
    };

    return errorMessages[errorCode] || 'An unexpected error occurred';
  }

  /**
   * Call an execution-state cloud function operation and normalize its outcome
   * @param {string} operation - API operation id
   * @param {Object} payload - Operation payload
   * @param {string} fallbackError - Error used when the function answers success: false
   * @param {string} defaultMessage - Message used when the function returns none
   * @returns {Promise<Object>} Result with success status and message
   * @throws {Error} Throws error with a user-friendly message for specific error codes
   */
  async callOperation(operation, payload, fallbackError, defaultMessage) {
    try {
      const callable = apiCallable(operation);

      const result = await callable(payload);

      if (result?.data?.success) {
        return {
          success: true,
          message: result.data.message || defaultMessage,
        };
      }

      // If success is false but no exception was thrown
      throw new Error(result?.data?.error || fallbackError);
    } catch (error) {
      // Handle Firebase Functions errors
      const errorCode = error?.code?.replace('functions/', '') || 'unknown';
      const mappedError = new Error(this.getErrorMessage(errorCode));
      mappedError.code = errorCode;

      throw mappedError;
    }
  }

  /**
   * Cancel an execution by calling the cloud function
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object>} Result with success status and message
   */
  async cancel(organizationId, divisionId, executionId) {
    return this.callOperation(
      this.cancelExecutionOp,
      { organizationId, divisionId, executionId },
      'Failed to cancel execution',
      'Execution canceled successfully',
    );
  }

  /**
   * Reports a delay. The stall document is written server-side because the security rules
   * only let the assigned worker write it, while the PWA lets a supervisor report it.
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @param {string} reason - Delay justification, required by the backend
   * @returns {Promise<Object>} Result with success status and message
   */
  async reportStall(organizationId, divisionId, executionId, reason) {
    return this.callOperation(
      this.reportStallOp,
      { organizationId, divisionId, executionId, reason },
      'Failed to report the delay',
      'Delay reported successfully',
    );
  }

  /**
   * Closes the active delay so the execution resumes.
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object>} Result with success status and message
   */
  async endStall(organizationId, divisionId, executionId) {
    return this.callOperation(
      this.endStallOp,
      { organizationId, divisionId, executionId },
      'Failed to end the delay',
      'Delay ended successfully',
    );
  }

  /**
   * Marks the execution done on behalf of its assignees (supervisor closure, ADR 0002).
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @param {string} reason - Why the work was not logged, required by the backend
   * @returns {Promise<Object>} Result with success status and message
   */
  async closeOnBehalf(organizationId, divisionId, executionId, reason) {
    return this.callOperation(
      this.supervisorCloseOp,
      { organizationId, divisionId, executionId, reason },
      'Failed to close execution',
      'Execution closed successfully',
    );
  }
}

export default ExecutionStateRepository;
